import {api} from "@/shared/api";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {addTimrBranding} from "@/shared/utils/misc";
import {AuthToken} from "@/shared/timr";

export class AddMobileDeviceDialog {

	public static EVENT_ADDED_MOBILE_DEVICE = "event_added_mobile_device";
	public static QRCODE_SIDE_LENGTH = 180;

	// duration in milliseconds
	public static QRCODE_FETCHING_USAGE_TIMEOUT = 1500; // timeout-duration between the api calls to check if the qr code it has been successfully scanned
	public static QRCODE_LOADING_DURATION = 1700; // duration for the loading icon, when the qr code has been successfully scanned
	public static FINISH_CLOSING_DURATION = 2500; // duration for the duration until the finish step is closed when the mobile device has been successfully connected

	private modal: BootstrapModal.Modal;
	private wizard: JQuery;

	private wizardResetted = false;
	private wizardFinished = false;
	private authToken: AuthToken = null;

	private periodicalExecutor: number;
	private deleteMobileDeviceConfigRequest: JQuery.jqXHR<void>;

	constructor() {
		this.initModal();
		this.initWizard();
		this.initListeners();
		this.initStoreButtons();
	}

	private initModal() {
		const modalId = $("#addMobileDeviceDialog");
		this.modal = new BootstrapModal.Modal({
			title: "devices.wizard.header",
			message: modalId,
			size: "modal-lg",
			footer: false,
			css: {
				content: "test-css"
			},
			onClose: () => {
				this.resetWizard();
				this.stopPeriodicalOneTimeAuthTokenUsageUpdate();

				if (this.qrCodeLoaded && this.wizardResetted && !this.wizardFinished) {
					this.deleteOneTimeToken();
				}

				if (this.wizardFinished) {
					$(this).triggerHandler(AddMobileDeviceDialog.EVENT_ADDED_MOBILE_DEVICE);
				}
			},
			onOpen: () => {
				this.wizardResetted = false;
				this.wizardFinished = false;
				this.authToken = null;

				this.deleteOneTimeToken(false);
				this.generateOneTimeAuthTokenQrCode();
				this.startPeriodicalOneTimeAuthTokenUsageUpdate();
			}
		});
		modalId.find("button[data-dismiss=modal]").click(() => this.resetWizard());
	}

	private initWizard() {
		this.wizard = $("#addMobileDeviceDialog > form").steps({
			headerTag: "h6",
			bodyTag: "fieldset",
			transitionEffect: "fade",
			cssClass: "wizard modal-wizard",
			enableCancelButton: true,
			enableFinishButton: false,
			enableKeyNavigation: false,
			titleTemplate: "<span class='number fa-mobile-devices-#index#'></span><span class='checkAfterCompleted'>#title#</span>",
			labels: {
				previous: messages["steps.previous"],
				next: messages["steps.next"],
				finish: messages["dialog.finish"],
				cancel: messages["dialog.cancel"]
			},
			onInit: (event: JQuery.TriggeredEvent) => {
				const form = $(event.target);
				addTimrBranding(form);
			},
			onStepChanged: (event: JQuery.TriggeredEvent, currentIndex: number) => {
				if (currentIndex === 2) {
					this.wizardFinished = true;
				}
			},
			onStepChanging: (event: JQuery.TriggeredEvent, currentIndex: number, newIndex: number) => {
				const form = $(event.target);
				const previousButton = form.find("a[href='#previous']").show();
				const cancelButton = form.find("a[href='#cancel']").show();
				const nextButton = form.find("a[href='#next']").show();

				if (newIndex === 2) {
					/*
						 * the terminal has successfully connected
						 * step backwards and cancel must be invisible and the steps state indicator be disabled
						 */
					setTimeout(() => {
						this.modal.close();
						this.resetWizard();
					}, AddMobileDeviceDialog.FINISH_CLOSING_DURATION);
					previousButton.hide();
					nextButton.hide();
					cancelButton.hide();
					Input.enable(this.modal.find("button[data-dismiss=modal]"));
					this.addLottieAnimation();
					this.deleteOneTimeToken();
				} else if (newIndex === 1) {
					this.wizard.steps("enableAria", false);
					Input.disable(nextButton.parent());
				} else if (newIndex === 0) {
					Input.enable(nextButton.parent());
					Input.enableByClass($(".emailSendBtn"));
				}
				return true;
			},
			onCanceled: () => {
				this.resetWizard();
				this.stopPeriodicalOneTimeAuthTokenUsageUpdate();
				this.deleteOneTimeToken();
				this.modal.close();
			}
		});

		const previousButton = this.wizard.find("a[href='#previous']");
		Input.disable(previousButton);
	}

	private deleteOneTimeToken(inclusiveApiRequest = true): JQueryPromise<boolean> {
		this.wizard.find(".addMobileDeviceDialogQrCode > img").remove();
		if (this.qrCodeLoaded && inclusiveApiRequest) {
			const deferred: JQueryDeferred<boolean> = $.Deferred();
			this.deleteMobileDeviceConfigRequest?.abort();
			this.deleteMobileDeviceConfigRequest = api("invalidateAuthToken.json")
				.post({
					token: this.authToken.uuid
				})
				.done(() => {
					this.authToken = null;
					deferred.resolve(true);
				})
				.fail(() => deferred.reject());
			return deferred.promise();
		} else {
			return $.Deferred<boolean>().resolve(true).promise();
		}
	}

	private generateOneTimeAuthTokenQrCode(): JQueryPromise<boolean> {
		const deferred: JQueryDeferred<boolean> = $.Deferred();

		if (this.qrCodeLoaded) {
			deferred.resolve(true);
		} else {
			api("generateOneTimeToken.json")
				.post()
				.done((authToken: AuthToken) => {
					this.authToken = authToken;
					const img = document.createElement("img");
					img.src = "/timr/oneTimeTokenQRCode.png?token=" + authToken.uuid;
					img.width = AddMobileDeviceDialog.QRCODE_SIDE_LENGTH;
					img.height = AddMobileDeviceDialog.QRCODE_SIDE_LENGTH;
					this.wizard.find(".addMobileDeviceDialogQrCode").append(img);
					img.onload = () => {
						deferred.resolve(true);
					};
				})
				.fail(() => {
					// TODO TIMR-5815
					console.log("failed");
				});
		}
		return deferred.promise();
	}

	private resetWizard() {
		FormUtil.removeAllFeedback(this.wizard);
		this.setOneTimeAuthTokenQrCodeLoadingState(false);
		this.setWizardButtonState(false);
		this.wizardResetted = true;
		this.wizard.steps("reset");
	}

	private get qrCodeLoaded(): boolean {
		return this.authToken !== null;
	}

	public open() {
		this.modal.show();
	}


	private initListeners() {
		window.onbeforeunload = () => {
			this.stopPeriodicalOneTimeAuthTokenUsageUpdate();
			if (this.qrCodeLoaded && !this.wizardFinished) {
				this.deleteOneTimeToken();
			}
		};

		const appLinkEmailButton = $(".emailSendBtn");
		appLinkEmailButton.on("click", (e) => {
			// Prevent double click to jump to the end
			// DEVNOTE: Input.disable() does not work here; the button is still clickable
			Input.disableByClass($(e.target));
			api("sendAppInstallLinkViaEmail.json")
				.post()
				.done(() => {
					new PNotify({
						text: messages["appLink.dialog.body.sendbutton.success"],
						addclass: "bg-success",
						buttons: {
							sticker: false
						}
					});
					this.wizard.steps("next");
				});
		});
	}

	private fetchOneTimeAuthTokenUsage() {
		api("isOneTimeTokenUsed.json")
			.post({
				token: this.authToken.uuid
			})
			.fail(() => {
				// TODO TIMR-5815
				console.log("fail");
			})
			.done((authTokenUsed: boolean) => {
				if (authTokenUsed) {
					this.mobileAuthTokenUsed();
				}
			});
	}

	private mobileAuthTokenUsed() {
		this.stopPeriodicalOneTimeAuthTokenUsageUpdate();
		setTimeout(() => this.wizard.steps("setStep", 2), AddMobileDeviceDialog.QRCODE_LOADING_DURATION);

		this.setOneTimeAuthTokenQrCodeLoadingState(true);
		this.setWizardButtonState(true);
	}

	private setOneTimeAuthTokenQrCodeLoadingState(loading: boolean) {
		this.wizard.find(".addMobileDeviceDialogQrCode").toggleClass("qrCodeLoading", loading);
	}

	private setWizardButtonState(setDisabled: boolean) {
		const liElement = this.wizard.find("ul[role=menu] > li");
		const aElement = liElement.find("> a");
		const dataDismiss = this.modal.find("button[data-dismiss=modal]");

		liElement.toggleClass("disabled", setDisabled);
		liElement.prop("aria-disabled", String(setDisabled));
		aElement.toggleClass("disabled", setDisabled);
		if (setDisabled) {
			Input.disable(dataDismiss);
		} else {
			Input.enable(dataDismiss);
		}
	}

	private startPeriodicalOneTimeAuthTokenUsageUpdate() {
		this.stopPeriodicalOneTimeAuthTokenUsageUpdate();
		this.periodicalExecutor = setInterval(() => this.fetchOneTimeAuthTokenUsage(), AddMobileDeviceDialog.QRCODE_FETCHING_USAGE_TIMEOUT);
	}

	private stopPeriodicalOneTimeAuthTokenUsageUpdate() {
		if (this.periodicalExecutor) {
			clearInterval(this.periodicalExecutor);
			this.periodicalExecutor = null;
		}
	}

	private initStoreButtons() {
		this.wizard.find(".play-store").prop("src", `/timr/images/stores/playstore-${globals.language}.svg`);
		this.wizard.find(".app-store").prop("src", `/timr/images/stores/appstore-${globals.language}.svg`);
	}

	private addLottieAnimation() {
		// remove the old animation if it exists to make sure it plays again and isn't doubled
		$(".successAnimation").remove();
		$(".successFullText")
			.prepend($("<lottie-player>")
				.prop("src", "/timr/lottie/success.json")
				.prop("autoplay", true)
				.prop("background", "transparent")
				.addClass("successAnimation"));
	}
}
